import axios from 'axios';
import { API_BASE_URL } from '@/config/api';

// Verrechenbare IPs kommen ausschliesslich aus den IP-Zuweisungen zur VM
// (/ip-assignments) — Agent-/Cloud-Init-IPs sind nicht verrechnungsrelevant.

// Kundennummer aus Pool-Root "K100562_..." ableiten; sonst null.
export const customerNumberFromPool = (pool) => {
  const root = String(pool || '').trim().split('/')[0] || '';
  const match = root.match(/^K(\d+)_/);
  return match ? Number(match[1]) : null;
};

export const formatVmPriceChf = (value) => {
  const num = Number(value);
  if (!Number.isFinite(num)) return '-';
  return `CHF ${num.toLocaleString('de-CH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

export const formatVmPriceDate = (isoDate) => {
  const [y, m, d] = String(isoDate || '').slice(0, 10).split('-');
  return y && m && d ? `${d}.${m}.${y}` : String(isoDate || '-');
};

// Vortag eines Stichtags ("gültig bis" = Tag vor dem nächsten validFrom).
export const formatVmPriceDayBefore = (isoDate) => {
  const date = new Date(`${String(isoDate || '').slice(0, 10)}T12:00:00`);
  if (Number.isNaN(date.getTime())) return '-';
  date.setDate(date.getDate() - 1);
  return `${String(date.getDate()).padStart(2, '0')}.${String(date.getMonth() + 1).padStart(2, '0')}.${date.getFullYear()}`;
};

const todayIso = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

// Admin: Zuordnung + Plan-Definitionen selbst auflösen (Antworten sind roh, nulls erben vom Default).
const resolveVmPricesetAdmin = async (customer, clusterId) => {
  const today = todayIso();
  const assignmentsRes = await axios.get(`${API_BASE_URL}/pricing/customer-plans`, {
    params: { customer },
    withCredentials: true,
  });
  const assignments = (assignmentsRes.data?.assignments || [])
    .slice()
    .sort((a, b) => String(a.validFrom).localeCompare(String(b.validFrom)));
  const assignmentAt = (date) => assignments
    .filter((a) => String(a.validFrom) <= date)
    .sort((a, b) => String(b.validFrom).localeCompare(String(a.validFrom)))[0] || null;
  const active = assignmentAt(today);

  const planDefsCache = new Map();
  const getPlanDefs = async (planId) => {
    const key = String(planId);
    if (!planDefsCache.has(key)) {
      const res = await axios.get(`${API_BASE_URL}/pricing/plans/${encodeURIComponent(planId)}`, { withCredentials: true });
      planDefsCache.set(key, Array.isArray(res.data?.definitions) ? res.data.definitions : []);
    }
    return planDefsCache.get(key);
  };

  // Preisset zum Stichtag auflösen: Felder erben vom Default, Overrides pro Storage gemergt (Cluster gewinnt).
  const resolveAt = (date, definitions) => {
    const pickActive = (cid) => definitions
      .filter((def) => String(def?.clusterId || '') === cid && String(def.validFrom) <= date)
      .sort((a, b) => String(b.validFrom).localeCompare(String(a.validFrom)))[0] || null;
    const defaultDef = pickActive('');
    const clusterDef = pickActive(clusterId);
    if (!defaultDef && !clusterDef) return null;
    const resolveField = (field) => {
      const clusterValue = clusterDef?.[field];
      if (clusterValue !== null && clusterValue !== undefined) return Number(clusterValue);
      const defaultValue = defaultDef?.[field];
      return defaultValue !== null && defaultValue !== undefined ? Number(defaultValue) : null;
    };
    const overrideMap = new Map();
    [defaultDef, clusterDef].forEach((def) => {
      (Array.isArray(def?.storagePrices) ? def.storagePrices : []).forEach((sp) => {
        const id = String(sp?.storageId || '').trim();
        if (id) overrideMap.set(id, { storageId: id, storagePricePer10Gb: sp.storagePricePer10Gb });
      });
    });
    return {
      baseVmPrice: resolveField('baseVmPrice'),
      cpuCorePrice: resolveField('cpuCorePrice'),
      ramGbPrice: resolveField('ramGbPrice'),
      publicIpPrice: resolveField('publicIpPrice'),
      windowsLicensePrice: resolveField('windowsLicensePrice'),
      storagePricePer10Gb: resolveField('storagePricePer10Gb'),
      storagePrices: [...overrideMap.values()].sort((a, b) => a.storageId.localeCompare(b.storageId)),
    };
  };

  const currentPrices = active ? resolveAt(today, await getPlanDefs(active.pricePlanId)) : null;

  // Alle künftigen Preisstände: chronologisch jede Zuordnung/Definition erfassen, die das Preisset ändert.
  const futures = [];
  try {
    const futureAssignments = assignments.filter((a) => String(a.validFrom) > today);
    const candidateDates = new Set(futureAssignments.map((a) => String(a.validFrom)));
    const planIds = [...new Set([active?.pricePlanId, ...futureAssignments.map((a) => a.pricePlanId)].filter(Boolean))];
    for (const planId of planIds) {
      (await getPlanDefs(planId))
        .filter((def) => {
          const cid = String(def?.clusterId || '');
          return (cid === '' || cid === clusterId) && String(def.validFrom) > today;
        })
        .forEach((def) => candidateDates.add(String(def.validFrom)));
    }
    let previousPrices = currentPrices;
    for (const date of [...candidateDates].sort()) {
      const assignment = assignmentAt(date);
      if (!assignment) continue;
      const prices = resolveAt(date, await getPlanDefs(assignment.pricePlanId));
      if (!prices) continue;
      if (!previousPrices || JSON.stringify(prices) !== JSON.stringify(previousPrices)) {
        futures.push({ validFrom: date, planName: assignment.pricePlanName || '', prices });
        previousPrices = prices;
      }
    }
  } catch (_) { /* Zukunftsinfo ist optional */ }

  if (!currentPrices) return { noPlan: true, futures };

  return {
    planName: active.pricePlanName || '',
    prices: currentPrices,
    futures,
  };
};

// Kunde: /pricing/my liefert bereits aufgelöste Preise pro Cluster (inkl. "upcoming"-Preisständen).
const resolveVmPricesetMy = async (customer, clusterId) => {
  const res = await axios.get(`${API_BASE_URL}/pricing/my`, { withCredentials: true });
  const entry = (res.data?.customers || []).find((c) => Number(c?.customerNumber) === customer);
  if (!entry) return null; // Kunde gehört nicht zum eingeloggten User
  if (!entry.pricePlan) return { noPlan: true };
  const prices = Array.isArray(entry.prices) ? entry.prices : [];
  const priceEntry = prices.find((p) => String(p?.clusterId || '') === clusterId)
    || prices.find((p) => !String(p?.clusterId || ''));
  if (!priceEntry) return { noPlan: true };
  const futures = (Array.isArray(priceEntry.upcoming) ? priceEntry.upcoming : [])
    .map((upcoming) => ({
      validFrom: String(upcoming?.validFrom || ''),
      planName: String(upcoming?.pricePlanName || ''),
      prices: upcoming,
    }))
    .filter((future) => future.validFrom);
  return { planName: entry.pricePlan.name || '', prices: priceEntry, futures };
};

// Liefert { planName, prices, futures } | { noPlan: true, futures? } | null (kein Zugriff).
export const resolveVmPriceset = async ({ isAdmin, customer, clusterId }) => (
  isAdmin
    ? resolveVmPricesetAdmin(customer, clusterId)
    : resolveVmPricesetMy(customer, clusterId)
);

// Aufschlüsselung für eine VM-Spezifikation berechnen.
// spec: { chargeCompute, cores, ramGb, disks: [{key,storage,sizeGb}], publicIpCount, windowsServer }
export const computeVmPriceBreakdown = (priceset, spec) => {
  if (!priceset || !spec) return null;
  const num = (v) => (v === null || v === undefined || v === '' ? null : Number(v));
  const round2 = (v) => Math.round(v * 100) / 100;
  const chargeCompute = spec.chargeCompute !== false;
  const rows = [];

  const base = num(priceset.baseVmPrice);
  if (base !== null) rows.push({ label: 'Basis', detail: 'pro VM', amount: base });

  if (chargeCompute) {
    const cores = Number(spec.cores) || 0;
    const cpuPrice = num(priceset.cpuCorePrice);
    if (cpuPrice !== null && cores > 0) {
      rows.push({ label: 'CPU', detail: `${cores} Core${cores === 1 ? '' : 's'} × ${formatVmPriceChf(cpuPrice)}`, amount: cores * cpuPrice });
    }
    const ramGb = Number(spec.ramGb) || 0;
    const ramPrice = num(priceset.ramGbPrice);
    if (ramPrice !== null && ramGb > 0) {
      rows.push({ label: 'RAM', detail: `${Math.round(ramGb * 10) / 10} GB × ${formatVmPriceChf(ramPrice)}`, amount: ramGb * ramPrice });
    }
  }

  const storageList = Array.isArray(priceset.storagePrices) ? priceset.storagePrices : [];
  // Preis pro Storage: konkreter Override, sonst Storage-Default-Preis der Definition.
  const storagePriceFor = (storageId) => {
    const override = storageList.find((sp) => String(sp?.storageId || '') === String(storageId || ''));
    const value = override?.storagePricePer10Gb ?? priceset.storagePricePer10Gb;
    return value !== null && value !== undefined ? Number(value) : null;
  };
  (Array.isArray(spec.disks) ? spec.disks : []).forEach((disk) => {
    const sizeGb = Number(disk?.sizeGb) || 0;
    if (sizeGb <= 0) return;
    const storage = String(disk?.storage || '').trim();
    const label = disk?.key ? `Disk (${disk.key})` : 'Disk';
    const price = storagePriceFor(storage);
    if (price === null) {
      // Preis fehlt im Plan — sichtbar machen statt still weglassen.
      rows.push({ label, detail: `${Math.round(sizeGb)} GB auf ${storage || '–'} – kein Storage-Preis definiert`, amount: null });
      return;
    }
    // Verrechnung immer auf volle 10 GB aufgerundet (23 GB → 30 GB).
    const billedUnits = Math.ceil(sizeGb / 10);
    const billedGb = billedUnits * 10;
    const billedHint = billedGb !== Math.round(sizeGb) ? ` (verrechnet ${billedGb} GB)` : '';
    rows.push({
      label,
      detail: `${Math.round(sizeGb)} GB${billedHint} auf ${storage || 'Storage'} × ${formatVmPriceChf(price)}/10 GB`,
      amount: billedUnits * price,
    });
  });

  const ipPrice = num(priceset.publicIpPrice);
  const ipCount = Number(spec.publicIpCount) || 0;
  if (ipPrice !== null && ipCount > 0) {
    rows.push({ label: 'IP-Zuweisungen', detail: `${ipCount} × ${formatVmPriceChf(ipPrice)}`, amount: ipCount * ipPrice });
  }

  if (chargeCompute && spec.windowsServer) {
    const winPrice = num(priceset.windowsLicensePrice);
    if (winPrice !== null) rows.push({ label: 'Windows-Server-Lizenz', detail: '1 Lizenz', amount: winPrice });
  }

  const total = round2(rows.reduce((sum, row) => sum + (row.amount ?? 0), 0));
  return {
    rows: rows.map((row) => ({ ...row, amount: row.amount === null ? null : round2(row.amount) })),
    total,
    chargeCompute,
  };
};
